<!--
  Client Portal v2: Overview dashboard (PR D3, aligned to the v2 design).

  Layout (handoff_2026-06-24/.../design/sections-core.jsx Overview), top to
  bottom:
    0. When ONE store is scoped: its close date, days-to-close countdown and
       "Extend close date" (StoreCloseDateCard; portal parity with the legacy
       Admin Buyer dashboard's "Days Until Close" tile).
    1. Four top stat cards (Jake-confirmed, KEPT distinct from v2's "Allowance
       used" 4th card):
         a. Total Sales    + "<X> last 30 days" sub
         b. Total Orders   + "<X> last 30 days" sub
         c. Avg Order      + "<X> last 30 days" sub
         d. Pending Approvals (the awaiting_approval count)
    2. Funding mix -- a FULL-WIDTH row directly below the cards (3-band
       proportion bar + legend). KEPT; v2 has no funding-mix card and we want
       both.
    3. "Your stores" leaderboard -- the v2 grid: # rank / name / SELF-scaled
       sparkline / stacked 30d-spend label / N orders. The sparkline conveys
       each store's own TREND (self-scaled per store), the dollar figure
       conveys MAGNITUDE -- so the old cross-store normalization is gone.
    4. "Top products" | "Order activity" SIDE-BY-SIDE (1fr 1fr; stacks on
       mobile):
         - Top products: the v2 inline format `<name> ... $<spend> . <qty>`.
         - Order activity: the 3-band BandedColumnChart, switched from
           per-MONTH to per-DAY (last 30 days, 30 buckets) with spread axis
           labels "30 days ago / 15 days ago / Today".

  The PR-D2 "Recent orders" block is REMOVED -- the Orders page owns that, and
  this view no longer fetches /api/portal/orders.

  Wired to GET /api/portal/overview. All figures are client-scoped server-side
  (403 on a non-client caller); cents are converted to dollars at render.
-->
<template>
  <v-container fluid class="portal-overview" data-testid="portal-page-overview">
    <h1 class="text-h5 mb-4">Overview</h1>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      data-testid="overview-loading"
    />

    <v-alert
      v-else-if="errorMsg"
      type="error"
      variant="tonal"
      data-testid="overview-error"
    >
      {{ errorMsg }}
    </v-alert>

    <template v-else>
      <!-- 0. The scoped store's close date + "Extend close date" (portal
           parity with the legacy Admin Buyer dashboard). Only for ONE store:
           the "All stores" aggregate has no single close date. The row is the
           scope store's /portal/stores entry; a successful extend hands the
           server's refreshed row back to the scope so every reader updates. -->
      <StoreCloseDateCard
        v-if="scope.selectedStore"
        class="mb-4"
        :store="scope.selectedStore"
        @updated="scope.updateStore($event)"
      />

      <!-- 1. Top-line cards: Total Sales / Total Orders / Avg Order / Pending Approvals -->
      <v-row dense>
        <!-- The headline stat block is scoped to the last 30 days (matching
             the funding-mix card + the activity chart below). The big figure IS
             the 30d figure, so the caption states the window rather than
             repeating the same number a second time. -->
        <v-col cols="12" sm="6" md="3">
          <v-card class="pa-4" data-testid="overview-tile-total-sales">
            <div class="text-caption text-medium-emphasis">Total sales</div>
            <div class="text-h4 mt-1">
              {{ formatDollars(metrics.total_sales_cents) }}
            </div>
            <div class="text-caption text-medium-emphasis mt-1">
              last 30 days
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="pa-4" data-testid="overview-tile-order-count">
            <div class="text-caption text-medium-emphasis">Total orders</div>
            <div class="text-h4 mt-1">{{ metrics.order_count }}</div>
            <div class="text-caption text-medium-emphasis mt-1">
              last 30 days
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="pa-4" data-testid="overview-tile-aov">
            <div class="text-caption text-medium-emphasis">Avg order</div>
            <div class="text-h4 mt-1">
              {{ formatDollars(metrics.average_order_value_cents) }}
            </div>
            <div class="text-caption text-medium-emphasis mt-1">
              last 30 days
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="pa-4" data-testid="overview-tile-pending-approvals">
            <div class="text-caption text-medium-emphasis">
              Pending approvals
            </div>
            <div class="text-h4 mt-1">{{ metrics.awaiting_approval }}</div>
            <div class="text-caption text-medium-emphasis mt-1">
              awaiting your sign-off
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- 2. Share of sales by funding source: full-width row below the cards.

           This card shows how sales were PAID (the split across the budget /
           allowance / external funding sources) as a share of total sales -- a
           payment-source MIX. It is NOT budget utilization (spent vs. cap);
           that lives on the Budget & Allowance page. Scoped to the last 30 days
           to match the headline stat cards above. -->
      <v-card class="mt-4 pa-4" data-testid="overview-funding-mix">
        <div class="d-flex align-center justify-space-between mb-1">
          <span class="text-subtitle-1">Share of sales by funding source</span>
          <BandLegend />
        </div>
        <div class="text-caption text-medium-emphasis mb-2">
          How your last 30 days of sales were paid
        </div>
        <BandStackedBar
          :bucket="metrics.funding_mix"
          testid="overview-funding-mix-bar"
          empty-label="No sales yet -- nothing to break down by funding source"
        />
        <div class="d-flex flex-wrap mt-3" style="gap: 24px">
          <span
            v-for="band in fundingMixRows"
            :key="band.key"
            class="text-body-2"
            :data-testid="`overview-funding-mix-${band.key}`"
          >
            <strong>{{ band.label }}-funded:</strong>
            {{ formatDollars(band.cents) }} - {{ band.pct }}% of sales
          </span>
        </div>
      </v-card>

      <!-- 3. Your stores leaderboard: # / name / self-scaled spark / 30d spend / N orders -->
      <v-card class="mt-4 pa-4" data-testid="overview-store-leaderboard">
        <div class="text-subtitle-1 mb-1">Your stores</div>
        <div class="text-body-2 text-medium-emphasis mb-3">
          Spend by store, last 30 days.
        </div>
        <div
          v-if="!storeRows.length"
          class="text-body-2 text-medium-emphasis"
          data-testid="overview-store-leaderboard-empty"
        >
          No store activity yet.
        </div>
        <div v-else class="store-rows">
          <div
            v-for="(store, i) in storeRows"
            :key="store.store_id"
            class="store-row"
            :class="{ 'store-row--clickable': !scope.locked }"
            :data-testid="`overview-store-row-${store.store_id}`"
            :role="scope.locked ? undefined : 'button'"
            :tabindex="scope.locked ? undefined : 0"
            :aria-label="
              scope.locked
                ? undefined
                : `Scope to ${store.store_name || 'store'}`
            "
            @click="scopeToStore(store.store_id)"
            @keydown.enter="scopeToStore(store.store_id)"
            @keydown.space.prevent="scopeToStore(store.store_id)"
          >
            <span class="store-row__rank text-caption">#{{ i + 1 }}</span>
            <span class="store-row__name text-body-2">{{
              store.store_name || "Unnamed store"
            }}</span>
            <!-- SELF-scaled: no shared-max, each store fills its own cell. -->
            <StoreSparkline
              class="store-row__spark"
              :values="store.spark"
              :testid="`overview-store-spark-${store.store_id}`"
            />
            <span class="store-row__spend">
              <span class="store-row__spend-value text-body-2">{{
                formatDollars(store.total_cents)
              }}</span>
              <span class="store-row__spend-label text-caption text-medium-emphasis"
                >30d spend</span
              >
            </span>
            <span class="store-row__orders text-caption text-medium-emphasis"
              >{{ store.order_count }} orders</span
            >
          </div>
        </div>
      </v-card>

      <!-- 4. Top products | Order activity, side by side -->
      <v-row class="mt-0">
        <v-col cols="12" md="6">
          <v-card class="pa-4 h-100" data-testid="overview-top-products">
            <div class="text-subtitle-1 mb-1">Top products</div>
            <div class="text-body-2 text-medium-emphasis mb-3">
              Most-ordered across your stores.
            </div>
            <div
              v-if="!topProducts.length"
              class="text-body-2 text-medium-emphasis"
              data-testid="overview-top-products-empty"
            >
              No products ordered yet.
            </div>
            <div v-else class="top-products">
              <div
                v-for="p in topProducts"
                :key="p.group_key"
                class="top-product-row"
                :data-testid="`overview-top-product-${p.group_key}`"
              >
                <span class="top-product-row__name text-body-2">{{
                  p.product_name || "Unnamed product"
                }}</span>
                <span class="top-product-row__dots" aria-hidden="true" />
                <span class="top-product-row__metric text-body-2">
                  <span class="top-product-row__spend">{{
                    formatDollars(p.spend_cents)
                  }}</span>
                  <span class="top-product-row__qty text-medium-emphasis">
                    &middot; {{ p.quantity }}</span
                  >
                </span>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="pa-4 h-100" data-testid="overview-series">
            <div class="d-flex align-center justify-space-between mb-3">
              <span class="text-subtitle-1">Order activity</span>
              <BandLegend />
            </div>
            <BandedColumnChart
              :buckets="metrics.daily_series"
              label-key="date"
              :axis-labels="ACTIVITY_AXIS_LABELS"
              testid="overview-series"
              plot-height="160px"
            >
              <template #empty>
                No banded spend to chart yet. Orders placed through your stores
                will appear here, split by budget, allowance, and external
                payment.
              </template>
            </BandedColumnChart>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import axios from "@/axios";
import BandStackedBar from "@/components/portal/BandStackedBar.vue";
import BandedColumnChart from "@/components/portal/BandedColumnChart.vue";
import BandLegend from "@/components/portal/BandLegend.vue";
import StoreSparkline from "@/components/portal/StoreSparkline.vue";
import StoreCloseDateCard from "@/components/portal/StoreCloseDateCard.vue";
import { BAND_DEFS } from "@/components/portal/bandColors";
import { usePortalScopeStore } from "@/stores/portalScope";

// Spread x-axis labels for the per-day "Order activity" chart (30 buckets:
// index 0 = 30 days ago, middle = 15 days ago, last = Today).
const ACTIVITY_AXIS_LABELS = ["30 days ago", "15 days ago", "Today"];

export default {
  name: "PortalOverview",
  components: {
    BandStackedBar,
    BandedColumnChart,
    BandLegend,
    StoreSparkline,
    StoreCloseDateCard,
  },
  setup() {
    // The GLOBAL header store scope (ADR 0055). Overview passes the selected
    // store_id to its endpoint so all rollups re-scope to that store; the
    // "Your stores" leaderboard rows also SET the scope on click.
    const scope = usePortalScopeStore();
    return { scope };
  },
  data() {
    return {
      ACTIVITY_AXIS_LABELS,
      metrics: {
        total_sales_cents: 0,
        funding_mix: {
          budget_cents: 0,
          allowance_cents: 0,
          external_cents: 0,
          budget_pct: 0,
          allowance_pct: 0,
          external_pct: 0,
        },
        banded_series: [],
        daily_series: [],
        order_count: 0,
        average_order_value_cents: 0,
        order_count_30d: 0,
        average_order_value_30d_cents: 0,
        store_leaderboard: [],
        top_products: [],
        orders_in_flight: 0,
        awaiting_approval: 0,
        spent_30d_cents: 0,
        remaining_budget_cents: null,
      },
      loading: true,
      errorMsg: "",
      // Monotonic fetch counter for the store-scope re-fetch race guard.
      _reqSeq: 0,
    };
  },
  computed: {
    fundingMixRows() {
      const mix = this.metrics.funding_mix || {};
      return BAND_DEFS.map((def) => ({
        key: def.key,
        label: def.label,
        cents: mix[def.centsKey] || 0,
        pct: mix[`${def.key}_pct`] ?? 0,
      }));
    },
    topProducts() {
      return this.metrics.top_products || [];
    },
    // Leaderboard rows. The per-store 30-day spend sparkline (`spark`) is
    // served by the backend; each StoreSparkline self-scales against its own
    // peak so a small store's trend is still legible next to a big one.
    storeRows() {
      const board = this.metrics.store_leaderboard || [];
      return board.map((store) => ({
        ...store,
        spark: Array.isArray(store.spark) ? store.spark : [],
      }));
    },
  },
  created() {
    this.fetchOverview();
  },
  watch: {
    // Re-fetch server-side whenever the global header store scope changes so
    // every rollup / chart / leaderboard re-scopes to the selected store.
    "scope.selectedStoreId"() {
      this.fetchOverview();
    },
  },
  methods: {
    // Leaderboard row affordance: clicking a store row sets the global scope
    // to that store (a no-op for a locked Store Manager). The natural way to
    // drill into a store from the "Your stores" leaderboard.
    scopeToStore(storeId) {
      if (this.scope.locked) return;
      this.scope.setStore(storeId);
    },
    async fetchOverview() {
      // Request-sequence guard against out-of-order responses when the store
      // scope changes rapidly (dropdown / leaderboard clicks). See Orders.vue.
      const seq = ++this._reqSeq;
      this.loading = true;
      this.errorMsg = "";
      try {
        const overviewResp = await axios.get("/api/portal/overview", {
          params: this.scope.storeIdParams,
        });
        if (seq !== this._reqSeq) return;
        if (overviewResp?.data) {
          // Merge onto the seeded default shape so a response missing a key
          // never leaves a tile/chart bound to undefined.
          this.metrics = { ...this.metrics, ...overviewResp.data };
          if (!this.metrics.funding_mix) {
            this.metrics.funding_mix = {
              budget_cents: 0,
              allowance_cents: 0,
              external_cents: 0,
              budget_pct: 0,
              allowance_pct: 0,
              external_pct: 0,
            };
          }
        }
      } catch (e) {
        if (seq !== this._reqSeq) return;
        this.errorMsg = "Could not load your dashboard.";
      } finally {
        if (seq === this._reqSeq) this.loading = false;
      }
    },
    formatDollars(cents) {
      return `$${((cents || 0) / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
};
</script>

<style scoped>
.store-rows {
  display: flex;
  flex-direction: column;
  gap: var(--tf-sp-2, 8px);
}
.store-row {
  display: grid;
  grid-template-columns: 28px 1fr 130px 130px 92px;
  align-items: center;
  gap: var(--tf-sp-3, 12px);
  padding: var(--tf-sp-2, 8px) var(--tf-sp-3, 12px);
  background: var(--tf-surface-alt, #fafbfc);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: var(--tf-radius-sm, 8px);
}
/* Clickable leaderboard row (sets the global store scope). Only applied for a
 * Client Admin -- a locked Store Manager cannot re-scope, so the row is inert. */
.store-row--clickable {
  cursor: pointer;
}
.store-row--clickable:hover {
  border-color: var(--portal-brand-color, var(--tf-border-strong, #b0b8c0));
  background: var(--tf-surface-light, var(--tf-surface-alt, #fafbfc));
}
.store-row--clickable:focus-visible {
  outline: 2px solid var(--portal-brand-color, #3b82f6);
  outline-offset: 1px;
}
.store-row__rank {
  color: var(--tf-fg-4, #757575);
  font-family: var(--tf-font-mono, ui-monospace, monospace);
}
.store-row__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}
.store-row__spend {
  text-align: right;
  display: flex;
  flex-direction: column;
}
.store-row__spend-value {
  font-weight: 600;
}
.store-row__orders {
  text-align: right;
}

/* Top products -- v2 inline "<name> ... $<spend> . <qty>" rows. */
.top-products {
  display: flex;
  flex-direction: column;
}
.top-product-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 9px 0;
}
.top-product-row + .top-product-row {
  border-top: 1px solid var(--tf-border, #e0e0e0);
}
.top-product-row__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 0 1 auto;
}
.top-product-row__dots {
  flex: 1 1 auto;
  border-bottom: 1px dotted var(--tf-border, #e0e0e0);
  align-self: center;
  margin: 0 2px;
}
.top-product-row__metric {
  flex: 0 0 auto;
  white-space: nowrap;
}
.top-product-row__spend {
  font-weight: 600;
  font-family: var(--tf-font-mono, ui-monospace, monospace);
}
.top-product-row__qty {
  font-family: var(--tf-font-mono, ui-monospace, monospace);
}

@media (max-width: 600px) {
  .store-row {
    grid-template-columns: 1fr auto;
    grid-template-areas:
      "name orders"
      "spark spend";
    row-gap: 6px;
  }
  .store-row__rank {
    display: none;
  }
  .store-row__name {
    grid-area: name;
  }
  .store-row__spark {
    grid-area: spark;
  }
  .store-row__spend {
    grid-area: spend;
  }
  .store-row__orders {
    grid-area: orders;
    text-align: right;
  }
}
</style>
